import React, { useMemo, useState } from "react";
import AuthInput from "../../../components/Input/AuthInput";
import CustomPagination from "../../../components/TablePagination/TablePagination";
import { FiChevronDown, FiInbox, FiUser } from "react-icons/fi";
import {
  buildCustomerItemSummary,
  filterCustomerSummary,
} from "../../../utils/reportCustomerSummary";

const formatRs = (n) =>
  Number(n || 0).toLocaleString("en-PK", { maximumFractionDigits: 0 });

const CustomerSummaryCard = ({ group, expanded, onToggle }) => {
  const totalQty = group.items.reduce((sum, item) => sum + Number(item.qty || 0), 0);
  const shoppingAmount = Number(group.shopping_amount || 0);
  const npiCredit = Number(group.npi_credit_total || 0);
  const npiDebit = Number(group.npi_debit_total || 0);

  const recordTypeLabel = (rec) => {
    if (rec.is_npi_credit) return "NPI Credit";
    if (rec.is_npi_debit) return "NPI Debit";
    if (rec.is_return) return "Return";
    return "Sale";
  };

  const recordBadgeClass = (rec) => {
    if (rec.is_npi_credit) return "bg-infoSoft text-info";
    if (rec.is_npi_debit) return "bg-warningSoft text-warning";
    if (rec.is_return) return "bg-dangerSoft text-danger";
    return "bg-successSoft text-emerald-800";
  };

  const recordIsNegative = (rec) => rec.is_return || rec.is_npi_debit;

  return (
    <div className="rounded-xl border border-line bg-card shadow-soft overflow-hidden">
      <button
        type="button"
        onClick={onToggle}
        className="w-full flex flex-wrap items-start justify-between gap-3 p-4 text-left hover:bg-subtle/80 transition-colors"
      >
        <div className="min-w-0 flex items-start gap-3">
          <div className="w-10 h-10 rounded-xl bg-brandSoft flex items-center justify-center shrink-0">
            <FiUser className="text-main" size={18} />
          </div>
          <div>
            <p className="text-sm font-bold text-ink">
              {group.customer_name}
              {group.shopping_wala ? ` / ${group.shopping_wala}` : ""}
            </p>
            <p className="text-xs text-muted">
              {group.sale_count} sale{group.sale_count !== 1 ? "s" : ""}
              {group.return_count > 0 && (
                <>
                  {" "}
                  · {group.return_count} return{group.return_count !== 1 ? "s" : ""}
                </>
              )}
              {group.npi_credit_count > 0 && (
                <>
                  {" "}
                  · {group.npi_credit_count} NPI Cr
                </>
              )}
              {group.npi_debit_count > 0 && (
                <>
                  {" "}
                  · {group.npi_debit_count} NPI Dr
                </>
              )}{" "}
              · {group.items.length} item{group.items.length !== 1 ? "s" : ""} · Qty{" "}
              {formatRs(totalQty)}
              {shoppingAmount !== 0 && (
                <>
                  {" "}
                  · Shopping Rs {formatRs(shoppingAmount)}
                </>
              )}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3 flex-wrap justify-end">
          {npiCredit !== 0 && (
            <div className="text-right">
              <p className="text-[10px] uppercase tracking-wide text-info font-semibold">
                NPI Credit
              </p>
              <p className="text-sm font-bold text-info tabular-nums">
                Rs {formatRs(npiCredit)}
              </p>
            </div>
          )}
          {npiDebit !== 0 && (
            <div className="text-right">
              <p className="text-[10px] uppercase tracking-wide text-warning font-semibold">
                NPI Debit
              </p>
              <p className="text-sm font-bold text-warning tabular-nums">
                Rs {formatRs(npiDebit)}
              </p>
            </div>
          )}
          {shoppingAmount !== 0 && (
            <div className="text-right">
              <p className="text-[10px] uppercase tracking-wide text-warning font-semibold">
                Shopping (net)
              </p>
              <p className="text-sm font-bold text-warning tabular-nums">
                Rs {formatRs(shoppingAmount)}
              </p>
            </div>
          )}
          <div className="text-right">
            <p className="text-[10px] uppercase tracking-wide text-muted font-semibold">
              Net total
            </p>
            <p
              className={`text-sm font-bold tabular-nums ${
                Number(group.total_amount) < 0 ? "text-danger" : "text-main"
              }`}
            >
              Rs {formatRs(group.total_amount)}
            </p>
          </div>
          <FiChevronDown
            className={`text-muted transition-transform duration-200 ${
              expanded ? "rotate-180" : ""
            }`}
          />
        </div>
      </button>

      {expanded && (
        <div className="border-t border-line px-4 pb-4 pt-3 space-y-4">
          {(group.invoice_records || []).length > 0 && (
            <div className="overflow-x-auto rounded-lg border border-line">
              <table className="w-full text-left text-sm">
                <thead className="bg-subtle text-xs uppercase text-muted">
                  <tr>
                    <th className="px-3 py-2 font-bold whitespace-nowrap">Date & time</th>
                    <th className="px-3 py-2 font-bold">Type</th>
                    <th className="px-3 py-2 font-bold">Invoice #</th>
                    <th className="px-3 py-2 font-bold whitespace-nowrap">Auto #</th>
                    <th className="px-3 py-2 font-bold text-right">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {group.invoice_records.map((rec) => (
                    <tr
                      key={`${rec.id}-${rec.invoice_no}-${recordTypeLabel(rec)}`}
                      className={`border-t border-line ${
                        recordIsNegative(rec) ? "bg-dangerSoft/50" : ""
                      }`}
                    >
                      <td className="px-3 py-2 text-inkSecondary whitespace-nowrap tabular-nums">
                        {rec.timestamp}
                      </td>
                      <td className="px-3 py-2">
                        <span
                          className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${recordBadgeClass(
                            rec
                          )}`}
                        >
                          {recordTypeLabel(rec)}
                        </span>
                      </td>
                      <td className="px-3 py-2 font-semibold text-ink tabular-nums">
                        {rec.invoice_no}
                        {rec.page_no ? ` · Pg ${rec.page_no}` : ""}
                      </td>
                      <td className="px-3 py-2 font-semibold text-inkSecondary tabular-nums">
                        {rec.auto_invoice_no != null && rec.auto_invoice_no !== ""
                          ? `#${rec.auto_invoice_no}`
                          : "—"}
                      </td>
                      <td
                        className={`px-3 py-2 text-right tabular-nums font-medium ${
                          recordIsNegative(rec) ? "text-danger" : "text-ink"
                        }`}
                      >
                        {recordIsNegative(rec) ? "−" : ""}Rs {formatRs(rec.total_amount)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {(npiCredit !== 0 || npiDebit !== 0) && (
            <div className="grid grid-cols-2 gap-2 text-sm">
              <div className="rounded-lg border border-info/20 bg-infoSoft/40 px-3 py-2">
                <p className="text-[10px] uppercase tracking-wide text-info font-semibold">
                  NPI Credit total
                </p>
                <p className="font-bold text-info tabular-nums">
                  Rs {formatRs(npiCredit)}
                </p>
              </div>
              <div className="rounded-lg border border-warning/20 bg-warningSoft/40 px-3 py-2">
                <p className="text-[10px] uppercase tracking-wide text-warning font-semibold">
                  NPI Debit total
                </p>
                <p className="font-bold text-warning tabular-nums">
                  Rs {formatRs(npiDebit)}
                </p>
              </div>
            </div>
          )}

          <div className="overflow-x-auto rounded-lg border border-line">
            <table className="w-full text-left text-sm">
              <thead className="bg-subtle text-xs uppercase text-muted">
                <tr>
                  <th className="px-3 py-2 font-bold">Item</th>
                  <th className="px-3 py-2 font-bold">Unit</th>
                  <th className="px-3 py-2 font-bold text-right">Net Qty</th>
                  <th className="px-3 py-2 font-bold text-right">Net Amount</th>
                </tr>
              </thead>
              <tbody>
                {group.items.map((item) => {
                  const isShopping = String(item.unit || "").trim().toUpperCase() === "SH";
                  const qtyNeg = Number(item.qty) < 0;
                  const amtNeg = Number(item.amount) < 0;
                  return (
                    <tr
                      key={`${group.customer_id}-${item.itemId}-${item.name}`}
                      className={`border-t border-line ${isShopping ? "bg-warningSoft/60" : ""}`}
                    >
                      <td className="px-3 py-2 font-semibold text-ink">
                        {item.name}
                      </td>
                      <td
                        className={`px-3 py-2 ${
                          isShopping ? "font-semibold text-warning" : "text-muted"
                        }`}
                      >
                        {item.unit}
                        {isShopping ? " · Shopping" : ""}
                      </td>
                      <td
                        className={`px-3 py-2 text-right tabular-nums font-bold ${
                          qtyNeg ? "text-danger" : "text-main"
                        }`}
                      >
                        {formatRs(item.qty)}
                      </td>
                      <td
                        className={`px-3 py-2 text-right tabular-nums font-medium ${
                          amtNeg ? "text-danger" : ""
                        }`}
                      >
                        Rs {formatRs(item.amount)}
                      </td>
                    </tr>
                  );
                })}
                {group.items.length === 0 && (
                  <tr>
                    <td
                      colSpan={4}
                      className="px-3 py-6 text-center text-muted text-sm"
                    >
                      No product lines — NPI only for this period.
                    </td>
                  </tr>
                )}
              </tbody>
              <tfoot className="bg-subtle border-t border-line">
                {shoppingAmount !== 0 && (
                  <tr>
                    <td
                      colSpan={2}
                      className="px-3 py-2 text-xs font-bold uppercase text-warning"
                    >
                      Shopping (SH unit, net)
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums font-bold text-warning">
                      —
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums font-bold text-warning">
                      Rs {formatRs(shoppingAmount)}
                    </td>
                  </tr>
                )}
                {npiCredit !== 0 && (
                  <tr>
                    <td
                      colSpan={2}
                      className="px-3 py-2 text-xs font-bold uppercase text-info"
                    >
                      NPI Credit
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums text-muted">
                      —
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums font-bold text-info">
                      Rs {formatRs(npiCredit)}
                    </td>
                  </tr>
                )}
                {npiDebit !== 0 && (
                  <tr>
                    <td
                      colSpan={2}
                      className="px-3 py-2 text-xs font-bold uppercase text-warning"
                    >
                      NPI Debit
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums text-muted">
                      —
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums font-bold text-warning">
                      −Rs {formatRs(npiDebit)}
                    </td>
                  </tr>
                )}
                <tr>
                  <td colSpan={2} className="px-3 py-2 text-xs font-bold uppercase text-muted">
                    Combined net
                  </td>
                  <td
                    className={`px-3 py-2 text-right tabular-nums font-extrabold ${
                      totalQty < 0 ? "text-danger" : "text-main"
                    }`}
                  >
                    {formatRs(totalQty)}
                  </td>
                  <td
                    className={`px-3 py-2 text-right tabular-nums font-extrabold ${
                      Number(group.total_amount) < 0 ? "text-danger" : ""
                    }`}
                  >
                    Rs {formatRs(group.total_amount)}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

const ReportCustomerSummary = ({
  salesInvoices = [],
  returnInvoices = [],
  npiInvoices = [],
  page,
  rowsPerPage,
  onPageChange,
  onRowsPerPageChange,
  searchText,
  setSearchText,
}) => {
  const [expandedKey, setExpandedKey] = useState(null);

  const summaryGroups = useMemo(
    () => buildCustomerItemSummary(salesInvoices, returnInvoices, npiInvoices),
    [salesInvoices, returnInvoices, npiInvoices]
  );

  const filtered = useMemo(
    () => filterCustomerSummary(summaryGroups, searchText),
    [summaryGroups, searchText]
  );

  const grandTotal = useMemo(
    () =>
      summaryGroups.reduce(
        (sum, group) => sum + Number(group.total_amount || 0),
        0
      ),
    [summaryGroups]
  );

  const grandShopping = useMemo(
    () =>
      summaryGroups.reduce(
        (sum, group) => sum + Number(group.shopping_amount || 0),
        0
      ),
    [summaryGroups]
  );

  const grandNpiCredit = useMemo(
    () =>
      summaryGroups.reduce(
        (sum, group) => sum + Number(group.npi_credit_total || 0),
        0
      ),
    [summaryGroups]
  );

  const grandNpiDebit = useMemo(
    () =>
      summaryGroups.reduce(
        (sum, group) => sum + Number(group.npi_debit_total || 0),
        0
      ),
    [summaryGroups]
  );

  const hasData =
    salesInvoices.length > 0 ||
    returnInvoices.length > 0 ||
    npiInvoices.length > 0;

  if (!hasData) {
    return (
      <div className="flex flex-col items-center justify-center py-16 px-4 rounded-2xl border-2 border-dashed border-line bg-subtle/80">
        <div className="w-16 h-16 rounded-2xl bg-brandSoft flex items-center justify-center mb-4">
          <FiInbox className="text-main" size={28} />
        </div>
        <p className="font-bold text-inkSecondary text-lg">No invoices to summarize</p>
        <p className="text-sm text-muted mt-1 text-center max-w-md">
          No customer sales, returns, or NPI found for this date range.
        </p>
      </div>
    );
  }

  const paged = filtered.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  return (
    <div className="flex flex-col w-full gap-3">
      {summaryGroups.length > 0 && (
        <div className="mb-1 flex flex-wrap items-center justify-between gap-3 rounded-lg border border-main/20 bg-brandSoft px-4 py-3">
          <p className="text-sm font-semibold text-main">
            Customer sales, returns & NPI — net (returns & NPI debit subtracted)
          </p>
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm tabular-nums font-bold">
            <span className="text-main">
              {summaryGroups.length} customer{summaryGroups.length !== 1 ? "s" : ""}
            </span>
            {grandNpiCredit !== 0 && (
              <span className="text-info">NPI Cr Rs {formatRs(grandNpiCredit)}</span>
            )}
            {grandNpiDebit !== 0 && (
              <span className="text-warning">NPI Dr Rs {formatRs(grandNpiDebit)}</span>
            )}
            <span className="text-main">Net Rs {formatRs(grandTotal)}</span>
            <span className="text-warning border-l border-warning/30 pl-4">
              Shopping Rs {formatRs(grandShopping)}
            </span>
          </div>
        </div>
      )}

      <div className="flex flex-col justify-end items-end gap-2 mb-1">
        <AuthInput
          Value={searchText}
          setValue={setSearchText}
          placeholder="Search customer, item, or invoice #"
        />
      </div>

      {filtered.length === 0 ? (
        <div className="w-full text-center text-muted py-8 text-sm">
          No matches for your search.
        </div>
      ) : (
        paged.map((group) => {
          const key = String(group.customer_id || group.customer_name);
          return (
            <CustomerSummaryCard
              key={key}
              group={group}
              expanded={expandedKey === key}
              onToggle={() =>
                setExpandedKey((prev) => (prev === key ? null : key))
              }
            />
          );
        })
      )}

      <CustomPagination
        count={filtered.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={onPageChange}
        RowsPerPage={rowsPerPage}
        onRowsPerPageChange={onRowsPerPageChange}
      />
    </div>
  );
};

export default ReportCustomerSummary;
